import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import type { Instrument, ResearchCase } from '../../api/client'
import { label } from '../../pages/researchVocabulary'
import s from './Suche.module.scss'

/** Ein Treffer der Suche — Instrument oder Fall, mit dem Ziel des Klicks. */
interface Treffer {
  key: string
  gruppe: 'Instrumente' | 'Fälle'
  name: string
  neben: string
  zeile2: string
  to: string
}

/** Ab wie vielen Zeichen gesucht wird — die Listen verlangen zwei. */
const MINDESTENS = 2

/** Wie lange nach dem letzten Zeichen gewartet wird, bevor gesucht wird. */
const WARTEN_MS = 180

/** „⌘K" auf dem Mac, „Strg K" sonst — was auf der Tastatur steht. */
export function kuerzel(): string {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  return mac ? '⌘K' : 'Strg K'
}

function alsTreffer(instrumente: Instrument[], faelle: ResearchCase[]): Treffer[] {
  return [
    ...instrumente.map((i): Treffer => ({
      key: `i-${i.id}`, gruppe: 'Instrumente', name: i.name, neben: i.isin,
      zeile2: i.primary_exchange, to: `/instruments/${i.id}`,
    })),
    ...faelle.map((f): Treffer => ({
      key: `f-${f.id}`, gruppe: 'Fälle', name: f.title, neben: f.reference ?? '',
      zeile2: `${label('case_type', f.case_type)} · ${label('case_status', f.status)}`,
      to: `/research/${f.id}`,
    })),
  ]
}

/**
 * Der Auslöser in der Kopfzeile: sieht aus wie ein Suchfeld, ist ein Knopf.
 * Die eigentliche Eingabe steht im Dialog, damit sie überall dieselbe ist.
 */
export function SucheKnopf({ onOpen }: { onOpen: () => void }) {
  return (
    <button type="button" className={s.knopf} onClick={onOpen}
            aria-label="Suche öffnen" title={`Suche öffnen (${kuerzel()})`}>
      <span className={s.lupe} aria-hidden="true" />
      <span className={s.knopfText}>Bezeichnung, ISIN oder Fall</span>
      <kbd className={s.knopfKuerzel}>{kuerzel()}</kbd>
    </button>
  )
}

/**
 * Die Suche über Instrumente und Fälle — der Dialog hinter ⌘K.
 *
 * ZWEI LISTEN, eine Eingabe: Bezeichnung und ISIN treffen Instrumente, Titel
 * und Aktenzeichen treffen Fälle; beides läuft parallel, und was nicht
 * antwortet, fehlt still. Pfeiltasten wählen, Enter springt, Escape schließt.
 *
 * `eingebettet` zeigt den Kasten ohne Dialog — für die Vorschau im
 * Design-System, wo ein modaler Dialog über der Karte läge.
 */
export function Suche({ offen, onClose, eingebettet }: {
  offen: boolean
  onClose: () => void
  eingebettet?: boolean
}) {
  const navigate = useNavigate()
  const dialog = useRef<HTMLDialogElement>(null)
  const eingabe = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [treffer, setTreffer] = useState<Treffer[]>([])
  const [sucht, setSucht] = useState(false)
  const [aktiv, setAktiv] = useState(0)

  // Auf und zu über showModal(): nur so liegt der Dialog in der obersten
  // Ebene, mit Fokusfalle und Escape.
  useEffect(() => {
    if (eingebettet) return
    const el = dialog.current
    if (!el) return
    if (offen && !el.open) { el.showModal(); setText(''); setTreffer([]); setAktiv(0) }
    if (!offen && el.open) el.close()
  }, [offen, eingebettet])

  useEffect(() => { if (offen) eingabe.current?.focus() }, [offen])

  // Suchen, sobald der Text steht — mit kurzer Wartezeit, damit nicht jeder
  // Buchstabe zwei Anfragen auslöst.
  useEffect(() => {
    const begriff = text.trim()
    if (begriff.length < MINDESTENS) { setTreffer([]); setSucht(false); return }
    const controller = new AbortController()
    const { signal } = controller
    const timer = window.setTimeout(() => {
      setSucht(true)
      const weich = <T,>(p: Promise<T>) => p.catch(() => null)
      Promise.all([
        weich(api.masterData({ q: begriff, size: 6 }, signal)),
        weich(api.researchCases({ q: begriff, size: 5 }, signal)),
      ]).then(([instrumente, faelle]) => {
        if (signal.aborted) return
        setTreffer(alsTreffer(instrumente?.items ?? [], faelle?.items ?? []))
        setAktiv(0)
      }).finally(() => { if (!signal.aborted) setSucht(false) })
    }, WARTEN_MS)
    return () => { window.clearTimeout(timer); controller.abort() }
  }, [text])

  function springe(ziel: Treffer) {
    onClose()
    navigate(ziel.to)
  }

  function onKey(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') { event.preventDefault(); setAktiv((a) => Math.min(a + 1, treffer.length - 1)) }
    if (event.key === 'ArrowUp') { event.preventDefault(); setAktiv((a) => Math.max(a - 1, 0)) }
    if (event.key === 'Enter' && treffer[aktiv]) { event.preventDefault(); springe(treffer[aktiv]) }
  }

  const begriff = text.trim()
  const kasten = (
    <div className={s.kasten}>
      <div className={s.eingabe}>
        <span className={s.lupe} aria-hidden="true" />
        <input ref={eingabe} value={text} onChange={(e) => setText(e.target.value)}
               onKeyDown={onKey} placeholder="Bezeichnung, ISIN oder Fall"
               aria-label="Suche" autoComplete="off" />
        {sucht && <span className={s.sucht}>sucht …</span>}
      </div>
      <div className={s.liste} role="listbox" aria-label="Treffer">
        {begriff.length < MINDESTENS && (
          <div className={s.hinweis}>Mindestens zwei Zeichen — ein Name, eine ISIN, ein Aktenzeichen.</div>
        )}
        {begriff.length >= MINDESTENS && !sucht && treffer.length === 0 && (
          <div className={s.hinweis}>Nichts gefunden zu „{begriff}“.</div>
        )}
        {(['Instrumente', 'Fälle'] as const).map((gruppe) => {
          const eigene = treffer.filter((t) => t.gruppe === gruppe)
          if (eigene.length === 0) return null
          return (
            <div key={gruppe}>
              <div className={s.gruppe}>{gruppe}</div>
              {eigene.map((t) => {
                const index = treffer.indexOf(t)
                return (
                  <button type="button" key={t.key} className={s.treffer}
                          role="option" aria-selected={index === aktiv}
                          data-aktiv={index === aktiv || undefined}
                          onMouseEnter={() => setAktiv(index)} onClick={() => springe(t)}>
                    <span className={s.name}>{t.name}</span>
                    <span className={s.neben}>{t.neben}</span>
                    <span className={s.zeile2}>{t.zeile2}</span>
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
      <div className={s.fuss}>
        <span><kbd>↑↓</kbd> wählen</span>
        <span><kbd>↵</kbd> öffnen</span>
        <span><kbd>esc</kbd> schließen</span>
      </div>
    </div>
  )

  if (eingebettet) return kasten
  return (
    <dialog ref={dialog} className={s.dialog} onClose={onClose}
            onClick={(e) => { if (e.target === dialog.current) onClose() }}>
      {kasten}
    </dialog>
  )
}
